import React, {useMemo} from 'react'
import PropTypes from 'prop-types'
import {Box, Toast as ChakraToast, useToastStyles} from '@chakra-ui/react3'
import {useIntl} from 'react-intl'
import {
    Alert02FilledIcon,
    CancelIcon,
    CheckmarkCircleFilledIcon,
    InformationCircleFilledIcon
} from '../icons'
import {TOAST_STATUS_VARIANT_MAP} from '../../theme/slot-recipes/toast'

/**
 * Xleap Toast Component
 *
 * Toast component conforming to Xleap Design System.
 * - Required: title and close button
 * - Optional: status icon (only shown when icon prop is passed), description, action
 * - Colour variants: default (black), error, success
 */
const ToastHeader = ({icon, title}) => {
    const styles = useToastStyles()

    return (
        <Box css={styles.header}>
            {icon && (
                <Box css={styles.indicator} data-testid="toast-indicator">
                    {icon}
                </Box>
            )}
            {title &&
                (typeof title === 'string' ? (
                    <ChakraToast.Title as="div" dangerouslySetInnerHTML={{__html: title}} />
                ) : (
                    <ChakraToast.Title as="div">{title}</ChakraToast.Title>
                ))}
        </Box>
    )
}

ToastHeader.propTypes = {
    icon: PropTypes.node,
    title: PropTypes.node
}

const Toast = ({
    title,
    description,
    type = 'default',
    icon: iconProp,
    action,
    isClosable = true,
    onClose
}) => {
    const intl = useIntl()
    const variant = TOAST_STATUS_VARIANT_MAP[type] || 'default'

    const icon = useMemo(() => {
        if (!iconProp) {
            return null
        }
        if (iconProp !== true) {
            return iconProp
        }
        if (type === 'success') {
            return <CheckmarkCircleFilledIcon boxSize="full" />
        }
        if (type === 'error' || type === 'warning') {
            return <Alert02FilledIcon boxSize="full" />
        }
        return <InformationCircleFilledIcon boxSize="full" />
    }, [iconProp, type])

    const closeLabel = intl.formatMessage({
        id: 'global.close',
        defaultMessage: 'Close'
    })

    return (
        <ChakraToast.Root variant={variant} role="alert">
            {(icon || title) && <ToastHeader icon={icon} title={title} />}
            {description && (
                <ChakraToast.Description as="div">{description}</ChakraToast.Description>
            )}
            {action && <ChakraToast.ActionTrigger as="div">{action}</ChakraToast.ActionTrigger>}
            {isClosable && (
                <ChakraToast.CloseTrigger type="button" aria-label={closeLabel} onClick={onClose}>
                    <CancelIcon boxSize="4" />
                </ChakraToast.CloseTrigger>
            )}
        </ChakraToast.Root>
    )
}

Toast.propTypes = {
    title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    description: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    type: PropTypes.oneOf(['success', 'error', 'warning', 'info', 'default']),
    icon: PropTypes.oneOfType([PropTypes.node, PropTypes.bool]),
    action: PropTypes.node,
    isClosable: PropTypes.bool,
    onClose: PropTypes.func
}

export default Toast
